import ExperimentSummary, { type SummaryData } from '../ExperimentSummary';
import {
  FlowContainer,
  FlowNode,
  FlowArrow,
  FlowTerminal,
  FlowDecision,
  FlowNote,
} from '../FlowPrimitives';

// ============================================================================
// Experiment 1 - Emission Spectra of Elements
// Cross-referenced with CHEM2131 Lab Manual, Experiment 1
// ============================================================================

const summaryData: SummaryData = {
  experimentId: 'exp1',
  title: 'Emission Spectra of Elements',
  objectives: [
    'Observe the emission spectra of various elements (H, He, Ne/Ar)',
    'Calibrate a prism spectrometer using helium known wavelengths',
    'Measure wavelengths of hydrogen spectral lines (Red, Blue, Purple, Purple weak)',
    'Calculate the Rydberg constant (1.09678 x 10^7 m^-1) from experimental data',
  ],
  safetyNotes: [
    'HIGH VOLTAGE: Do not touch discharge tubes while powered on',
    'LAMP MAY BECOME HOT - allow it to cool for a few minutes before switching it out',
    'ONLY handle the bulbs by the larger ends, not the slim centre piece',
    'UV radiation may be emitted - do not stare directly at tubes',
    'Lift spectrometer using the base only, not the arms - keep it level in transport',
  ],
  equipment: [
    'Prism spectrometer with Vernier scale',
    'Gas discharge tubes (hydrogen, helium, neon or argon)',
    'High voltage power supply (lamp)',
    'Wood clamp (to secure spectrometer to bench)',
    'Cloth (to cover prism and tube ends)',
    'Excel template (from DC Connect) for wavelength prediction',
  ],
  chemicals: [
    'Hydrogen gas (sealed discharge tube)',
    'Helium gas (sealed discharge tube)',
    'Neon or Argon gas (sealed discharge tube)',
  ],
  procedure: [
    {
      number: 1,
      title: 'Part A: Spectrometer Calibration',
      details: [
        'Set up the spectrometer on a level surface',
        'Align the telescope arm with the prism',
        'Use a known light source to verify scale readings',
      ],
      manualRef: 'Lab Manual - Part A',
    },
    {
      number: 2,
      title: 'Part B: Hydrogen Spectrum',
      details: [
        'Install the hydrogen discharge tube in the power supply',
        'Power on the high voltage supply',
        'Observe the pink-purple glow of the tube',
        'Rotate the telescope to find each spectral line',
        'Record the angle for each visible line',
        'Identify lines: Red (~656 nm), Blue (~486 nm), Purple (~434 nm), Purple weak (~410 nm)',
      ],
      safetyNote: 'CAUTION: High voltage - do not touch the tube or power supply contacts',
      manualRef: 'Lab Manual - Part B',
    },
    {
      number: 3,
      title: 'Part C: Calculations',
      details: [
        'Convert measured angles to wavelengths using the calibration curve',
        'Identify the electron transitions (all end at n=2 for Balmer series)',
        'Calculate the Rydberg constant (R_H) for each spectral line using the Rydberg equation',
        'Average your R_H values from all lines',
        'Compare to the accepted value (1.09678 x 10^7 m^-1)',
        'Calculate percent error',
      ],
      manualRef: 'Lab Manual - Part C',
    },
    {
      number: 4,
      title: 'Part D: Comparison Spectra',
      details: [
        'Replace the hydrogen tube with the helium discharge tube',
        'Observe and record the different spectral line pattern for helium',
        'Repeat with the neon or argon discharge tube - count lines, note colours and spacing',
        'Compare the three spectra - note differences in line positions and intensities',
      ],
      safetyNote: 'Allow tubes to cool before swapping',
      manualRef: 'Lab Manual - Part D',
    },
  ],
  keyFormulas: [
    '1/λ = R_H × (1/n₁\u00B2 - 1/n₂\u00B2)  [Rydberg Equation]',
    'E_n = -13.6/n\u00B2 eV  [Energy of level n for hydrogen]',
    'R_H = 1.09678 × 10⁷ m⁻\u00B9  [Accepted Rydberg constant]',
    '% Error = |Experimental - Accepted| / Accepted × 100',
  ],
  tips: [
    'The room should be as dark as possible for best observation of spectral lines',
    'Let your eyes adjust to darkness for 1-2 minutes before reading the spectrometer',
    'The purple lines (Purple, Purple weak) are dim - look carefully',
    'Record angles carefully; small errors lead to large wavelength errors',
    'Each element has a unique emission fingerprint - this is the basis of spectroscopic identification',
  ],
};

// ============================================================================
// Flowchart: Emission Spectra Procedure
// Layout: vertical flow, 600px wide, nodes centered at x=210 (width 180)
// ============================================================================

function Flowchart() {
  // Horizontal center for nodes: (600 - 180) / 2 = 210
  const cx = 210;
  const nw = 180; // node width
  const nh = 56;  // node height
  const tw = 140; // terminal width
  const th = 36;  // terminal height
  // Terminal center: (600 - 140) / 2 = 230
  const tx = 230;

  return (
    <FlowContainer width={600} height={700} title="Emission Spectra Procedure Flow">
      {/* Start */}
      <FlowTerminal x={tx} y={40} label="Start" color="#6366f1" />

      {/* Arrow: Start -> Setup */}
      <FlowArrow from={{ x: 300, y: 76 }} to={{ x: 300, y: 96 }} />

      {/* Step 1: Setup Spectrometer */}
      <FlowNode x={cx} y={96} title="Setup Spectrometer" subtitle="Level surface, align prism" color="#0d9488" />

      {/* Arrow: Setup -> Calibrate */}
      <FlowArrow from={{ x: 300, y: 152 }} to={{ x: 300, y: 172 }} />

      {/* Step 2: Calibrate */}
      <FlowNode x={cx} y={172} title="Calibrate" subtitle="Verify scale with known source" color="#0d9488" />

      {/* Arrow: Calibrate -> Observe H */}
      <FlowArrow from={{ x: 300, y: 228 }} to={{ x: 300, y: 248 }} />

      {/* Step 3: Observe Hydrogen */}
      <FlowNode x={cx} y={248} title="Observe H Spectrum" subtitle="Record angles for 4 Balmer lines" color="#2563eb" />

      {/* Safety note for high voltage */}
      <FlowNote x={420} y={258} text="HIGH VOLTAGE!" width={110} color="#ef4444" />

      {/* Arrow: Observe H -> Record Wavelengths */}
      <FlowArrow from={{ x: 300, y: 304 }} to={{ x: 300, y: 324 }} />

      {/* Step 4: Record Wavelengths */}
      <FlowNode x={cx} y={324} title="Record Wavelengths" subtitle="Convert angles via calibration" color="#0d9488" />

      {/* Arrow: Record -> Calculate Rydberg */}
      <FlowArrow from={{ x: 300, y: 380 }} to={{ x: 300, y: 400 }} />

      {/* Step 5: Calculate Rydberg */}
      <FlowNode x={cx} y={400} title="Calculate R_H" subtitle="Use Rydberg eq. for each line" color="#7c3aed" />

      {/* Arrow: Rydberg -> Decision */}
      <FlowArrow from={{ x: 300, y: 456 }} to={{ x: 300, y: 476 }} />

      {/* Decision: More gases? */}
      <FlowDecision x={265} y={476} size={60} label="More gases?" color="#f59e0b" />

      {/* Yes arrow: loop back right side -> Observe step (go to He, Ne) */}
      <FlowArrow
        from={{ x: 325, y: 506 }}
        to={{ x: 440, y: 506 }}
        label="Yes (He, Ne)"
        color="#f59e0b"
      />
      <FlowArrow
        from={{ x: 440, y: 506 }}
        to={{ x: 440, y: 268 }}
        color="#f59e0b"
      />
      <FlowArrow
        from={{ x: 440, y: 268 }}
        to={{ x: 390, y: 268 }}
        color="#f59e0b"
      />

      {/* No arrow: Decision -> Compare */}
      <FlowArrow from={{ x: 295, y: 536 }} to={{ x: 295, y: 556 }} label="No" color="#64748b" />

      {/* Step 6: Compare & Identify */}
      <FlowNode x={cx} y={556} title="Compare & Identify" subtitle="Compare spectra of H, He, Ne" color="#0d9488" />

      {/* Arrow: Compare -> End */}
      <FlowArrow from={{ x: 300, y: 612 }} to={{ x: 300, y: 640 }} />

      {/* End */}
      <FlowTerminal x={tx} y={640} label="End" color="#6366f1" />
    </FlowContainer>
  );
}

// ============================================================================
// Export
// ============================================================================

export default function Exp1Summary() {
  return <ExperimentSummary data={summaryData} flowchart={<Flowchart />} />;
}
